/**
 * "Not delivered" is five things, and this is where they stay five. Slice 10.
 *
 * A message the provider took and never delivered failed in one of five ways,
 * and the five differ in the only respect a person reading this screen cares
 * about: **who has to do something, and what.** A rendering failure is ours to
 * fix inside this product. A soft bounce is worth sending to again next week. A
 * hard bounce is finished and already suppressed. A rejection never left AWS. A
 * delay has not failed at all — SES is still trying. Added together they make
 * one number that tells a person only that something is wrong somewhere, which
 * is why `notDeliveredBreakdownSchema` has no `total` member and why nothing
 * here computes one.
 *
 * **Every count and every label is the contract's.** `NotDeliveredBreakdown`
 * comes from `@wend/shared`, built by `notDeliveredBreakdown()` from a single
 * `GROUP BY event_type, bounce_type` on the API side. This file owns exactly
 * one thing the wire cannot carry: the sentence that says what the reader can
 * do about each one. Those sentences are the point of the section — a row
 * reading "3 rendering_failure" is a log line, not an answer.
 *
 * **Rows with nobody in them are dropped**, the same rule `skipReasonRows`
 * follows one panel up. A zero beside "Template failed to render" on a campaign
 * whose template rendered perfectly is noise, and five permanent zeros teach a
 * person to skip the whole section on the campaign where one of them is not
 * zero. The zeros here are genuinely measured — `delivery_events` was read and
 * held no such row — so when all five are empty this says so in a sentence
 * rather than printing five noughts.
 *
 * **What is deliberately not here:** a sixth row for a bounce SES would not
 * classify. It is neither hard nor soft, so it can carry no "what to do about
 * it" sentence, and folding it into the soft bounces would claim it is worth
 * retrying when nobody knows that. It stays visible as `bounces.undetermined`
 * in the counters above this section, which is the honest place for it.
 *
 * **An empty breakdown is only an observation on a channel these five outcomes
 * exist on**, and slice 12 changed which predicate says so without changing the
 * rule. Slice 10 asked `isDeliveryMeasured`, and the reason it gave was that
 * `delivery_events` was written by the SES ingest and nothing else: on a
 * WhatsApp campaign the five zeros were not "SES reported no failures", they
 * were "there is no feedback of any kind to read", and the reassuring sentence
 * written for the email case would have turned every WhatsApp send that ever
 * happened into a clean delivery record.
 *
 * Meta's status webhook now writes rows, so `isDeliveryMeasured` is true on both
 * channels and would let this panel onto WhatsApp — where its zeros would be
 * *worse* than before, because they would now look like a measurement that had
 * actually been taken. **The gate is `isBounceMeasured`**, the predicate slice 12
 * split out for exactly this: four of these five outcomes are bounce or
 * SES-refusal events with no WhatsApp counterpart at all, and Meta reports every
 * non-delivery as one word with an error code beside it. `CampaignWaFailures` is
 * the equivalent panel over there, and the caller picks between the two — this
 * component's own branch is the second lock rather than the first.
 *
 * **Since slice 14 these rows have a second reader, and that is why
 * `NOT_DELIVERED_OUTCOMES` is exported.** The range screen at `/analytics` prints
 * the same five labels, tones, order and sentences over a whole period, so a
 * soft bounce cannot be described one way on a campaign and another way on a
 * month. What it does *not* reuse is this component: the empty state above is
 * written about one campaign, and a period holds many. Edit a sentence here and
 * both screens change, which is the intent — edit one there instead and the two
 * have started disagreeing.
 *
 * **These five count messages, and they still do not add up to anything.** The
 * API counts distinct `message_id`s per outcome rather than event rows, so one
 * message SES retried four times is one delayed message and not four. But a
 * message can honestly be in two rows — delayed for an hour and then hard
 * bounced — so the five overlap, they overlap the bounce counters above them
 * too, and totalling any of it produces a number that describes nothing. Hence
 * five rows, no total, and a sentence saying what the rows are counting rather
 * than a reader left to reconcile figures that were never meant to reconcile.
 *
 * ## Slice 17 cut every sentence here to one line, and did not cut one of them
 *
 * The five sentences used to run to three each: what the outcome is, what it
 * means for the contact, what to do, and — in two of them — why the obvious
 * repair is the wrong one. That is *four* facts where the row only ever had to
 * carry two, because the extra ones were written for a builder deciding whether
 * the split was worth having rather than for somebody looking at a campaign that
 * has just gone out. The argument for the split lives in this header now, which
 * is the only place it was ever needed.
 *
 * **What survives in every one of them is the verdict on whether the reader can
 * act**, because that is the entire reason the five are five. A rendering
 * failure is ours; a soft bounce is worth retrying; a hard bounce is finished; a
 * rejection needs something done in AWS; a delay needs nothing. Delete that
 * clause from any row and the row becomes a log line again, which is the
 * sentence this file's first paragraph was written to prevent.
 *
 * **Two clauses look like padding and are not.** *"Rather than cleaning them off
 * the list"* on the soft bounce, and *"repair the contact list, not this
 * campaign"* on the hard one, are the two places where a reader who has
 * understood the row can still take the wrong action — and they are opposite
 * actions, which is why the two rows most alike in wording are the two that most
 * need their tails. `CampaignWaFailures` keeps the same clause on `transient`
 * for the same reason.
 *
 * **The shortening reaches `/analytics` too**, by the mechanism the paragraph
 * above describes: those rows are these rows. That is still the intent — a soft
 * bounce described two ways is the defect the export exists to prevent — but it
 * does mean an edit here is an edit to two screens, and the range screen was not
 * re-read when this was cut. Nothing there renders a sentence this file does not
 * own.
 */
import { isBounceMeasured, type Channel, type NotDeliveredBreakdown } from '@wend/shared';
import { Badge, Kicker, type BadgeTone } from '@wend/ui';

import { formatCount } from '../../lib/format';

export interface NotDeliveredOutcome {
  readonly key: keyof NotDeliveredBreakdown;
  /** Two or three words, for a chip. */
  readonly label: string;
  /** Whether the reader can act, said in one sentence and in plain words. */
  readonly sentence: string;
  readonly tone: BadgeTone;
}

/**
 * The five, ordered from "you can fix this" to "there is nothing to do".
 *
 * The same ordering rule `SKIP_REASON_ORDER` uses, for the same reason: the
 * first line of a breakdown is the one people actually read, so it should be
 * the one they can act on. A rendering failure leads because it is the only one
 * of the five that is Wend's own fault and the only one repaired by editing
 * something inside this product. A delay is last because it is not a failure —
 * it is the one row where the correct action is to wait.
 *
 * Tones say how much attention a row deserves rather than restating its
 * severity: `delayed` is `info` because a delayed message may still arrive, and
 * painting the one row that is still in flight the same red as a dead address
 * would make a person clean a working address off their list.
 */
export const NOT_DELIVERED_OUTCOMES: readonly NotDeliveredOutcome[] = [
  {
    key: 'renderingFailed',
    label: 'Template failed to render',
    tone: 'warn',
    sentence:
      'Ours to fix — usually a merge field with nothing behind it. Correct the template and send again; no credit was consumed.',
  },
  {
    key: 'softBounced',
    label: 'Soft bounce',
    tone: 'warn',
    sentence:
      'Temporary — the mailbox was full, greylisted or throttled. Nobody was suppressed: send to these again later rather than cleaning them off the list.',
  },
  {
    key: 'rejected',
    label: 'Refused by Amazon',
    tone: 'bad',
    sentence:
      'Amazon refused these before they left — the content, or an address on Amazon’s own suppression list. Nothing will retry until that is dealt with in AWS.',
  },
  {
    key: 'hardBounced',
    label: 'Hard bounce',
    tone: 'bad',
    sentence:
      'The address does not exist, or refused permanently. Already suppressed and never sent to again — repair the contact list, not this campaign.',
  },
  {
    key: 'delayed',
    label: 'Still being attempted',
    tone: 'info',
    sentence:
      'Not a failure yet — Amazon is still trying. Nothing to do but wait; these can still turn into a delivery, or into a bounce, hours later.',
  },
];

export interface CampaignNotDeliveredProps {
  breakdown: NotDeliveredBreakdown;
  /**
   * The campaign's channel, because the empty state is a claim about the
   * channel and not only about the campaign — see the branch below.
   */
  channel: Channel;
}

export function CampaignNotDelivered({ breakdown, channel }: CampaignNotDeliveredProps) {
  const rows = NOT_DELIVERED_OUTCOMES.filter((outcome) => breakdown[outcome.key] > 0);

  /*
   * THE EMPTY STATE IS A MEASUREMENT, AND ONLY ON A CHANNEL THESE FIVE EXIST ON.
   *
   * Five zeros on an email campaign mean `delivery_events` was read and held no
   * failure — a real, useful negative. On WhatsApp the same five zeros are not a
   * negative at all: there is no rejection, no rendering failure and no bounce of
   * either temperature on that channel, so nothing could ever have been counted
   * into four of these five rows. Saying "nothing has come back from Amazon"
   * would be true and worthless, since Amazon never handled the campaign, and it
   * would read as a clean delivery record on every WhatsApp send there has ever
   * been.
   *
   * Since slice 12 the caller does not render this panel on WhatsApp at all —
   * `CampaignWaFailures` goes there instead. This branch stays because the
   * argument is about the channel and not about one call site, and because the
   * next channel added to this product will reach here before anybody remembers
   * to check: it fails to a sentence rather than to five reassuring noughts.
   */
  const measured = isBounceMeasured(channel);

  return (
    <div className="flex flex-col gap-3 border-t border-line pt-5">
      {/*
        SLICE 28: THE SECTION DRAWS ONLY WHEN THERE IS SOMETHING IN IT.

        Three things went, and the heading went with the two empty branches:

        — the not-measured branch was 40 words explaining a taxonomy mismatch to
          a reader on a channel where these five outcomes do not exist. Dead copy
          kept as a defensive fallback, addressed to nobody.
        — the empty branch was a heading plus 31 words to report that nothing bad
          happened, hedged with an epistemics disclaimer. A section whose entire
          content is "nothing is wrong" teaches a reader to skip sections.
        — the counting note explained `countDistinct(message_id)` against provider
          notifications and warned that the rows do not sum. Internal accounting
          rationale on a marketer's screen; the rows carry their own labels.

        The caller already applied this gate in the unreported branch. It is
        applied here so every mounting gets it rather than the one that
        remembered.
      */}
      {!measured || rows.length === 0 ? null : (
        <>
          <Kicker rule>
            Why messages did not arrive
          </Kicker>

          <ul className="flex flex-col">
            {rows.map((outcome) => (
              <li
                key={outcome.key}
                className="flex flex-col gap-2 border-t border-line py-3.5 first:border-t-0 first:pt-0"
              >
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  <span className="text-[1.05rem] font-semibold tabular-nums text-text">
                    {formatCount(breakdown[outcome.key])}
                  </span>
                  <Badge tone={outcome.tone}>{outcome.label}</Badge>
                </div>
                <p className="max-w-prose text-[0.88rem] leading-relaxed text-muted">
                  {outcome.sentence}
                </p>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
